<template>
  <div class="import-contacts flex flex-col">
    <woot-modal-header-v2 :header-title="$t('IMPORT_CONTACTS.TITLE')">
      <template #titleIcon>
        <v2-icon icon="download" class="text-gray-500" />
      </template>
      <div class="text-sm leading-5 text-gray-600 dark:text-slate-300 mt-2">
        {{ $t('IMPORT_CONTACTS.DESC') }}
        <a
          :href="csvUrl"
          download="import-contacts-sample"
          class="underline text-green-500 hover:text-green-600 transition-colors"
        >
          {{ $t('IMPORT_CONTACTS.DOWNLOAD_LABEL') }}
        </a>
      </div>
    </woot-modal-header-v2>

    <div class="px-6 py-4 flex flex-col gap-4">
      <div v-if="lastImportLines.length" class="flex flex-col gap-1.5">
        <div class="flex items-center gap-2">
          <span class="text-sm leading-5 text-gray-700 dark:text-slate-500">
            {{ $t('IMPORT_CONTACTS.LAST_RESULT.TITLE') }}
          </span>
          <woot-button
            color-scheme="secondary"
            variant="link"
            icon="ion-android-refresh"
            @click="fetchImportStatus"
          />
        </div>
        <div
          class="bg-gray-100 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 w-full overflow-auto max-h-32 text-base leading-6 text-gray-500 dark:text-slate-400 break-words"
        >
          <div v-for="(line, index) in lastImportLines" :key="index">
            {{ line.label }}:
            <bdi dir="auto" class="whitespace-pre-line">{{ line.value }}</bdi>
          </div>
        </div>
      </div>

      <input
        id="file"
        ref="file"
        type="file"
        accept=".csv,.xlsx"
        class="hidden"
        @change="handleFileUpload"
      />

      <div
        v-if="uploadState === 'idle'"
        class="flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-[26px] border-2 border-dashed bg-white py-11 transition-colors dark:bg-slate-900"
        :class="
          isDragging ? 'border-brand' : 'border-[#dfdcdc] hover:border-brand/40'
        "
        @click="$refs.file.click()"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <div class="flex flex-col items-center gap-2 px-4 text-center">
          <p class="dropzone-title">
            {{ $t('IMPORT_CONTACTS.FORM.DROPZONE_LABEL') }}
          </p>
          <p class="dropzone-subtitle">
            {{ $t('IMPORT_CONTACTS.FORM.DROPZONE_FILE_LIMIT') }}
          </p>
        </div>
      </div>

      <div
        v-else-if="uploadState === 'success'"
        class="relative flex items-start gap-3 rounded-[12px] border border-[#baf3db] bg-[#f0fdf4] p-6"
      >
        <div class="flex shrink-0 items-center rounded-[8px] bg-[#dcfff1] p-2">
          <v2-icon icon="note" class="text-brand-500" />
        </div>
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <p class="file-card-name truncate">{{ file.name }}</p>
          <p class="file-card-size">
            <span dir="ltr">{{ formattedFileSize }}</span>
          </p>
        </div>
        <button
          type="button"
          class="absolute right-[14px] top-[8px] cursor-pointer text-[#6b7280] transition-colors hover:text-primary rtl:right-auto rtl:left-[14px]"
          @click="clearFile"
        >
          <v2-icon icon="close_small" size="24" />
        </button>
      </div>

      <div
        v-else-if="uploadState === 'error'"
        class="relative flex flex-col gap-3 rounded-[12px] border border-danger-200 bg-danger-50 p-6"
      >
        <div class="flex items-start gap-3">
          <div
            class="flex shrink-0 items-center rounded-[8px] bg-danger-100 p-2"
          >
            <v2-icon icon="note" class="text-danger-500" />
          </div>
          <div class="flex min-w-0 flex-1 flex-col gap-1.5">
            <p class="file-card-name truncate">{{ file.name }}</p>
            <p class="file-card-size">
              <span dir="ltr">{{ formattedFileSize }}</span>
            </p>
          </div>
        </div>
        <p class="file-card-error">{{ fileError }}</p>
        <button
          type="button"
          class="absolute right-[13px] top-[8px] cursor-pointer text-[#6b7280] transition-colors hover:text-primary rtl:right-auto rtl:left-[13px]"
          @click="clearFile"
        >
          <v2-icon icon="close_small" size="24" />
        </button>
      </div>
    </div>

    <modal-footer-v2
      :cancel-text="$t('IMPORT_CONTACTS.FORM.CANCEL')"
      :confirm-text="$t('IMPORT_CONTACTS.FORM.SUBMIT')"
      :is-disabled="uiFlags.isCreating || uploadState !== 'success'"
      :is-loading="uiFlags.isCreating"
      @cancel="onClose"
      @confirm="uploadFile"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { CONTACTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import V2Icon from 'v2/components/Icon.vue';

export default {
  components: { V2Icon },
  mixins: [alertMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    this.fetchImportStatus();
    return {
      file: '',
      fileError: null,
      uploadState: 'idle',
      isDragging: false,
      lastImport: {},
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
    }),
    csvUrl() {
      return '/downloads/import-contacts-sample.xlsx';
    },
    lastImportLines() {
      const { status, started_at: startedAt } = this.lastImport;
      if (!status) return [];
      const message = this.lastImport.message || '';
      const label = key => this.$t(`IMPORT_CONTACTS.LAST_RESULT.${key}`);
      const lines = [];
      if (startedAt) {
        const utc = new Date(startedAt * 1000).toISOString();
        lines.push({
          label: label('CREATED_AT'),
          value: `${utc.slice(0, 10)} ${utc.slice(11, 19)} UTC`,
        });
      }
      lines.push({
        label: label('STATUS'),
        value: `'${label(`STATUSES.${status.toUpperCase()}`)}'`,
      });
      const [, processed] = message.match(/processed: (\S+)/) || [];
      if (processed) {
        lines.push({ label: label('PROCESSED'), value: processed });
      }
      const [, errors] = message.match(/errors: ([\s\S]*)$/) || [];
      if (errors) lines.push({ label: label('ERRORS'), value: errors });
      return lines;
    },
    formattedFileSize() {
      if (!this.file) return '';
      const bytes = this.file.size;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },
  },
  mounted() {
    this.$track(CONTACTS_EVENTS.IMPORT_MODAL_OPEN);
  },
  methods: {
    async uploadFile() {
      try {
        if (this.uploadState !== 'success' || !this.file) return;
        await this.$store.dispatch('contacts/import', this.file);
        this.onClose();
        this.showAlert(this.$t('IMPORT_CONTACTS.SUCCESS_MESSAGE'));
        this.$track(CONTACTS_EVENTS.IMPORT_SUCCESS);
      } catch (error) {
        this.showAlert(
          error.message || this.$t('IMPORT_CONTACTS.ERROR_MESSAGE')
        );
        this.$track(CONTACTS_EVENTS.IMPORT_FAILURE);
      }
    },
    processFile(pickedFile) {
      const name = (pickedFile.name || '').toLowerCase();
      const isValidType = name.endsWith('.csv') || name.endsWith('.xlsx');
      if (!isValidType) {
        this.file = pickedFile;
        this.fileError = this.$t(
          'IMPORT_CONTACTS.FORM.FILE_ERROR.INVALID_TYPE'
        );
        this.uploadState = 'error';
        return;
      }
      if (pickedFile.size > 10 * 1024 * 1024) {
        this.file = pickedFile;
        this.fileError = this.$t('IMPORT_CONTACTS.FORM.FILE_ERROR.TOO_LARGE');
        this.uploadState = 'error';
        return;
      }
      this.file = pickedFile;
      this.fileError = null;
      this.uploadState = 'success';
    },
    clearFile() {
      this.file = '';
      this.fileError = null;
      this.uploadState = 'idle';
      this.isDragging = false;
      if (this.$refs.file) this.$refs.file.value = '';
    },
    handleDrop(event) {
      this.isDragging = false;
      const dropped = event.dataTransfer.files[0];
      if (dropped) this.processFile(dropped);
    },
    handleFileUpload() {
      const picked = this.$refs.file.files[0];
      if (picked) this.processFile(picked);
    },
    async fetchImportStatus() {
      try {
        const lastImport = await this.$store.dispatch(
          'contacts/statusOfLastImport'
        );
        this.lastImport = lastImport || {};
      } catch (error) {
        this.showAlert(
          error.message ||
            this.$t('INBOX_MGMT.IMPORT_CONVERSATIONS.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style scoped>
.dropzone-title {
  font-size: 16px !important;
  line-height: 1.2 !important;
  color: #323431 !important;
  margin: 0 !important;
}

.dropzone-subtitle {
  font-size: 14px !important;
  line-height: 1.5 !important;
  color: #717171 !important;
  margin: 0 !important;
}

.file-card-name {
  font-size: 16px !important;
  line-height: 24px !important;
  font-weight: 400 !important;
  color: #323431 !important;
  margin: 0 !important;
}

.file-card-size {
  font-size: 14px !important;
  line-height: 20px !important;
  color: #5c5c5c !important;
  margin: 0 !important;
}

.file-card-error {
  font-size: 14px !important;
  line-height: 20px !important;
  color: #ef4444 !important;
  margin: 0 !important;
}
</style>
